// React
import React from 'react';

// Components
import AnimateButton from 'components/@extended/AnimateButton';
import { MainCard } from 'components';

// Packages
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
    Grid,
    Stack,
    Box,
    InputLabel,
    TextField,
    Autocomplete,
    CircularProgress,
    Button
} from '@mui/material';

// Styles
import { useTheme } from '@mui/material/styles';

// Utils
import { errorMessage, streetAddressRegExp } from 'utils/constants';
import { capitalizeFirstLetterMulti } from 'utils/Helpers';

const RegistrationAddress = ({
    loading,
    properties,
    setStep,
    setPropertyAddress,
    propertyAddress
}) => {
    const theme = useTheme();

    const sortByStreetNameThenNumber = propertiesList => {
        const extractParts = address => {
            const parts = address.toLowerCase().trim().split(/\s+/);
            const streetNumber = parseInt(parts[0], 10);
            const streetName = parts.slice(1).join(' ');
            return { streetName, streetNumber };
        };

        return [...propertiesList].sort((a, b) => {
            const aParts = extractParts(a.address);
            const bParts = extractParts(b.address);

            const nameCompare = aParts.streetName.localeCompare(bParts.streetName);
            if (nameCompare !== 0) return nameCompare;

            return aParts.streetNumber - bParts.streetNumber;
        });
    };

    const formSchema = Yup.object().shape({
        address: Yup.string()
            .trim()
            .required(errorMessage.address.required)
            .matches(streetAddressRegExp, errorMessage.address.valid)
    });

    const initialValues = {
        address: propertyAddress ? propertyAddress : ''
    };

    const submit = values => {
        setPropertyAddress(values.address);
        setStep('occupants');
    };

    const filterAddressOptions = (options, { inputValue }) => {
        const normalizedInput = inputValue.trim().toLowerCase();
        const inputStartsWithNumber = /^\d/.test(normalizedInput);
        const inputTokens = normalizedInput.split(/\s+/);

        return options.filter(option => {
            const address = option.address.toLowerCase().trim();
            const addressTokens = address.split(/\s+/);

            if (inputStartsWithNumber) {
                return inputTokens.every((token, idx) =>
                    addressTokens[idx]?.startsWith(token)
                );
            } else {
                return address.includes(normalizedInput);
            }
        });
    };

    if (!properties)
        return (
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(0,0,0,.5)'
                }}
            >
                <CircularProgress
                    size={60}
                    thickness={4}
                    sx={{ color: theme.palette.secondary.lighter }}
                />
            </Box>
        );

    return (
        <Grid
            item
            sm={8}
            sx={{
                backgroundColor: 'rgba(33, 35, 34, 0.05)',
                height: '100vh',
                padding: '40px'
            }}
        >
            <MainCard
                style={{ padding: '10px 30px', fontSize: '1rem', lineHeight: '1.6' }}
            >
                <h2
                    style={{
                        color: '#012D27',
                        fontSize: '1.8rem',
                        fontWeight: '600',
                        marginBottom: '20px',
                        textAlign: 'center'
                    }}
                >
                    Let&apos;s Get Started
                </h2>
                <p>
                    We&apos;re excited to welcome you to the{' '}
                    <span className={'oeSpan'}>SAGE</span> community! Follow these steps
                    to complete your enrollment:
                </p>
                <ol>
                    <li style={{ marginBottom: '15px' }}>
                        <span className={'oeSpan'}>Select Your Address:</span> Begin by
                        typing your home address in the search box. As you type, relevant
                        address options will appear. Choose the correct address from the
                        list to proceed.
                    </li>
                    <li style={{ marginBottom: '15px' }}>
                        <span className={'oeSpan'}>Enter Your Information:</span> Provide
                        your personal details, including your phone number, which will be
                        used to complete your account registration.
                    </li>
                    <li style={{ marginBottom: '15px' }}>
                        <span className={'oeSpan'}>Add Occupants:</span> If there are any
                        other members of your household, simply add them as additional
                        occupants.
                        <br />
                    </li>
                </ol>
                <p>
                    Once you submit the form, your community management team will verify
                    your information and promptly send your invitation.
                </p>
                <p>
                    Welcome to a new era of secure, convenient access with{' '}
                    <span className={'oeSpan'}>SAGE</span>!
                </p>

                <Box>
                    <Formik
                        initialValues={initialValues}
                        validationSchema={formSchema}
                        onSubmit={submit}
                        enableReinitialize
                    >
                        {({
                            values,
                            errors,
                            touched,
                            handleChange,
                            handleBlur,
                            handleSubmit,
                            setFieldValue
                        }) => (
                            <Grid container spacing={3} style={{ marginTop: '10px' }}>
                                <Grid item xs={12} sm={12}>
                                    <Stack spacing={0.5}>
                                        <InputLabel
                                            style={{
                                                fontWeight: '600',
                                                fontSize: '1rem',
                                                marginBottom: '5px'
                                            }}
                                        >
                                            Street Address
                                        </InputLabel>
                                        <Autocomplete
                                            id="address"
                                            name="address"
                                            options={sortByStreetNameThenNumber(
                                                properties || []
                                            )}
                                            getOptionLabel={option =>
                                                capitalizeFirstLetterMulti(option.address)
                                            }
                                            filterOptions={filterAddressOptions}
                                            isOptionEqualToValue={(option, value) =>
                                                option.id === value.id
                                            }
                                            value={
                                                (properties || []).find(
                                                    p => p.address === values.address
                                                ) || null
                                            }
                                            onChange={(_, newValue) => {
                                                setFieldValue(
                                                    'address',
                                                    newValue?.address || ''
                                                );
                                            }}
                                            onBlur={handleBlur}
                                            renderInput={params => (
                                                <TextField
                                                    {...params}
                                                    required
                                                    placeholder="Enter your street address"
                                                    fullWidth
                                                    autoComplete="shipping address-line1"
                                                    error={
                                                        touched.address &&
                                                        !!errors.address
                                                    }
                                                    helperText={
                                                        touched.address && errors.address
                                                    }
                                                />
                                            )}
                                        />
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} sm={12}>
                                    <Stack>
                                        <AnimateButton>
                                            <Button
                                                style={{ width: '100%' }}
                                                variant="contained"
                                                size="large"
                                                onClick={handleSubmit}
                                                disabled={loading}
                                            >
                                                Next
                                            </Button>
                                        </AnimateButton>
                                    </Stack>
                                </Grid>
                            </Grid>
                        )}
                    </Formik>
                </Box>
            </MainCard>
        </Grid>
    );
};

export default RegistrationAddress;
